import React, { useEffect, useRef, useState } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { MdMenu } from "react-icons/md";
import "./style.css";

export const DraggableListVertical = ({ data, updateItem, mode, handleSelected, onReorder, disabled = false, editingDisabled = false }) => {
  const [items, setItems] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}));

  useEffect(() => {
    setItems(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (disabled || !over) return;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(items, oldIndex, newIndex);
      setItems(reordered);
      onReorder?.(reordered);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
      <SortableContext items={items?.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <div
          style={{
            width: "100%",
            margin: "0 auto",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        >
          {items?.map((item) => (
            <SortableItem key={item.id} id={item.id} data={item} updateItem={updateItem} mode={mode} handleSelected={handleSelected} disabled={disabled} editingDisabled={editingDisabled} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export const parseAttributePrice = value => {
  const text = String(value ?? "").trim();
  if (!/^\d+(?:[.,]\d+)?$/.test(text)) return null;
  const price = Number(text.replace(",", "."));
  return Number.isFinite(price) && Number.isSafeInteger(Math.trunc(price)) && (price !== 0 || !/[1-9]/.test(text)) ? price : null;
};

const formatAttributePrice = value => value == null ? "" : String(value).replace(".",",");

const SortableItem = ({ id, data, updateItem, mode, handleSelected, disabled, editingDisabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });
  const [localName, setLocalName] = useState(data.name || "");
  const [localPrice, setLocalPrice] = useState(formatAttributePrice(data.price));
  const [saved, setSaved] = useState({name:data.name || "", price:Number(data.price)});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const busy = useRef(false);
  const dirty = localName !== saved.name || parseAttributePrice(localPrice) !== saved.price;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (!dirtyRef.current) {
      setLocalName(data.name || "");
      setLocalPrice(formatAttributePrice(data.price));
      setSaved({name:data.name || "", price:Number(data.price)});
    }
  }, [data.name, data.price]);

  const save = async () => {
    if (disabled || editingDisabled || busy.current || !dirty) return;
    const price = parseAttributePrice(localPrice);
    if (!localName.trim() || price === null) {
      setError("Escribe un nombre y un importe válido en euros, usando coma o punto decimal.");
      setNotice("");
      return;
    }
    busy.current = true;
    setSaving(true); setError(""); setNotice("");
    try {
      if (await updateItem(data.id, localName.trim(), price) !== true) throw new Error("save");
      setSaved({name:localName.trim(),price});
      setLocalName(localName.trim()); setLocalPrice(formatAttributePrice(price));
      setNotice("Guardado");
    } catch {
      setError("No se pudo guardar. Tus cambios se conservan; vuelve a intentarlo.");
    } finally { busy.current = false; setSaving(false); }
  };
  const changeActive = async active => {
    if (disabled || editingDisabled || busy.current) return;
    busy.current = true; setSaving(true); setError(""); setNotice("");
    try {
      if (await updateItem(data.id, data.name, data.price, active) !== true) throw new Error("activation");
    } catch { setError("No se pudo cambiar la disponibilidad. Vuelve a intentarlo."); }
    finally { busy.current = false; setSaving(false); }
  };
  const onEnter = event => { if (event.key === "Enter") { event.preventDefault(); save(); } };
  return <div ref={setNodeRef} className="attribute-editor-row" data-attribute-dirty={dirty} data-attribute-pending={saving} style={{transform:CSS.Transform.toString(transform),transition}}>
    {mode === "none" ? <button type="button" {...attributes} {...listeners} disabled={disabled} className="attribute-drag-handle" aria-label={`Reordenar ${data.name}`}><MdMenu size={20}/></button> : <input type="checkbox" onChange={()=>handleSelected(id)} aria-label={`Seleccionar ${data.name}`} className="custom-checkbox"/>}
    <div className="attribute-editor-name"><label htmlFor={`attribute-name-${id}`}>Nombre</label><input id={`attribute-name-${id}`} value={localName} disabled={saving || disabled || editingDisabled} onChange={e=>{setLocalName(e.target.value);setNotice("");}} onKeyDown={onEnter}/></div>
    <div className="attribute-editor-price"><label htmlFor={`attribute-price-${id}`}>Importe en euros</label><input id={`attribute-price-${id}`} type="text" inputMode="decimal" value={localPrice} disabled={saving || disabled || editingDisabled} aria-invalid={Boolean(error) && parseAttributePrice(localPrice) === null} aria-describedby={error ? `attribute-error-${id}` : undefined} onChange={e=>{setLocalPrice(e.target.value);setNotice("");}} onKeyDown={onEnter}/></div>
    <label className="attribute-editor-active"><input type="checkbox" checked={Boolean(data.isActive)} disabled={saving || disabled || editingDisabled} onChange={e=>changeActive(e.target.checked)}/>Disponible</label>
    <button type="button" className="customButton customButtonColor2" disabled={saving || disabled || editingDisabled || !dirty} aria-busy={saving} onClick={save}>{saving ? "Guardando…" : "Guardar"}<span className="visually-hidden"> {data.name}</span></button>
    {error && <p className="attribute-editor-message" id={`attribute-error-${id}`} role="alert">{error}</p>}
    {notice && <span className="attribute-editor-message" role="status">{notice}</span>}
  </div>;
};
